// =============================================================================
// src/views/shared/section-insight-card.ts — card de insight AO VIVO por seção.
//
// Surfacing da Camada 3 (gold.section_insight, MiniMax M3) DENTRO do feed. O
//   main.ts já funde o vivo sobre o estático em ctx.sectionInsights
//   (hydrateSectionInsights → withSectionInsights), mas até aqui SÓ a sidebar do
//   Copiloto consumia (chrome/copilot-sidebar). Resultado: ao rolar o feed, o
//   usuário via cópia estática do mockup — não o dado real do tenant (Omar Aziz).
//   Este módulo injeta o MESMO insight no topo de cada .u-section, fechando o gap.
//
// Fonte: ctx.sectionInsights?.[id] (vivo) ?? SECTION_INSIGHTS[id] (fallback estático).
// Reusa renderInsightCard (core/ui.ts) — mesma CSS (.u-insight-card), escape do
//   prompt e onclick sendPrompt (DRY; não reimplementa o markup do card).
// =============================================================================

import type { AppContext, SectionId, SectionInsight } from '../../app/types';
import { SECTION_INSIGHTS } from '../../data';
import { qs, escapeHtml } from '../../app/dom';
import { renderInsightCard } from '../../core/ui';

/** Ícone Tabler do card — sinaliza origem IA/Copiloto. */
const INSIGHT_ICON = 'ti-sparkles';

/** Insight da seção: vivo (gold.section_insight) → fallback estático (SECTION_INSIGHTS). */
export function resolveSectionInsight(ctx: AppContext, id: SectionId): SectionInsight {
  return ctx.sectionInsights?.[id] ?? SECTION_INSIGHTS[id];
}

/** Badge: rótulo da seção + confiança do modelo (ex.: "Mapa Eleitoral · IA 78%"). */
function insightBadge(insight: SectionInsight): string {
  return `${escapeHtml(insight.label)} · IA ${insight.confidence}%`;
}

/** Corpo: headline (texto, escapado) em destaque + detail (HTML inline, fiel ao original). */
function insightBody(insight: SectionInsight): string {
  return `<strong>${escapeHtml(insight.headline)}</strong><br>${insight.detail}`;
}

/**
 * HTML do card de insight ao vivo de UMA seção, embrulhado num marcador greppável
 * (data-section-insight) para idempotência e teste. Reusa renderInsightCard (core/ui).
 *
 * @example
 *   body.insertAdjacentHTML('afterbegin', renderSectionInsightCard('mapa-eleitoral', insight));
 */
export function renderSectionInsightCard(id: SectionId, insight: SectionInsight): string {
  const card = renderInsightCard(
    insightBadge(insight),
    insightBody(insight),
    insight.primaryPrompt,
    INSIGHT_ICON,
  );
  // data-ai-surface: gancho greppável p/ o registro de IA (src/data/ai-surfaces)
  //   e o /mapa-ia. O card já rotula "· IA {confidence}%", então não duplica chip.
  return `<div class="u-section-insight" data-section-insight="${id}" data-ai-surface="section-insight.feed-cards">${card}</div>`;
}

/** Injeta o card no topo do corpo da seção `id`, uma única vez (guarda idempotente). */
function mountOne(ctx: AppContext, id: SectionId, root: ParentNode): void {
  const body = qs(`.u-section[data-section="${id}"] .u-section-body`, root);
  if (!body || qs(`[data-section-insight="${id}"]`, body)) return;
  body.insertAdjacentHTML('afterbegin', renderSectionInsightCard(id, resolveSectionInsight(ctx, id)));
}

/**
 * Injeta o card de insight ao vivo no topo de cada seção do feed presente no DOM.
 * Seções sem markup estático (cinema) ou fora do feed (grafo) são puladas em silêncio
 * (mountOne não acha o .u-section-body). Chamado uma vez por main.ts após montar
 * #contentCol — main.ts é composition root (excluído da cobertura).
 */
export function mountSectionInsightCards(ctx: AppContext, root: ParentNode = document): void {
  (Object.keys(SECTION_INSIGHTS) as SectionId[]).forEach((id) => mountOne(ctx, id, root));
}
